import { translateBlock } from 'i18n';
import {
    BUDGET_BALANCE_TOTAL_FIELD,
    CHAPTER_LABEL_FIELD,
    REPORTED_FIELD,
} from 'routes/BudgetModule/config/constants';
import { ColDef, ValueFormatterParams } from 'ag-grid-community';
import {
    BUDGET_LINES_BALANCE_METRICS,
    INSCRIPTIONS_BALANCE_METRICS,
    ALL_INDICATORS,
} from './constants';
import { getFieldWithReportFromField } from './helpers';
import { currencyFormatter } from 'helpers/formatHelper';
import { CURRENCY_EXCEL_CLASSNAMES } from 'routes/BudgetModule/config/excelExportHelpers';
import { useTheme } from '@mui/material/styles';

export const getColumns = (
    exercice: number,
    t: TranslationT,
    isReportedAmountUsed: boolean,
    finalIndicatorColumnField: string,
): ColDef[] => {
    const numericColDef: ColDef<unknown, number> = {
        sortable: false,
        minWidth: 180,
        resizable: true,
        floatingFilterComponent: 'numberFloatingFilter',
        valueFormatter: (params: ValueFormatterParams<unknown, number>) => {
            return params.value != null ? currencyFormatter(params.value) : '';
        },
        useValueFormatterForExport: false,
        cellClass: CURRENCY_EXCEL_CLASSNAMES,
        cellStyle: {
            textAlign: 'right',
        },
        type: 'numericColumn',
    };
    const theme = useTheme();
    const backgroundColor = theme.palette.primary.background ?? '';
    const translatedIndicators = ALL_INDICATORS.map((i) => translateBlock(i));
    const baseColumns: ColDef[] = [
        {
            field: CHAPTER_LABEL_FIELD,
            headerName: translateBlock(CHAPTER_LABEL_FIELD),
            sortable: false,
            pinned: 'left' as const,
            minWidth: 350,
            resizable: true,
            cellRendererParams: ({
                data,
            }: {
                data: { [CHAPTER_LABEL_FIELD]: string };
            }) => ({
                colored: true,
                bold: ALL_INDICATORS.map((i) => translateBlock(i)).includes(
                    data[CHAPTER_LABEL_FIELD],
                ),
            }),
            cellRenderer: 'tooltipCellRenderer',
            cellStyle: ({
                data,
            }: {
                data: { [CHAPTER_LABEL_FIELD]: string };
            }) => {
                const colored = translatedIndicators.includes(
                    data[CHAPTER_LABEL_FIELD],
                );
                return {
                    backgroundColor: colored ? backgroundColor : '',
                };
            },
        },
        ...BUDGET_LINES_BALANCE_METRICS.map((field) => ({
            ...numericColDef,
            field,
            headerName: `${translateBlock(field)} ${exercice - 1}`,
            cellStyle: ({
                data,
            }: {
                data: { [CHAPTER_LABEL_FIELD]: string };
            }) => {
                const colored = translatedIndicators.includes(
                    data[CHAPTER_LABEL_FIELD],
                );
                return {
                    backgroundColor: colored ? backgroundColor : '',
                };
            },
        })),
        ...INSCRIPTIONS_BALANCE_METRICS.map((field) => ({
            ...numericColDef,
            field,
            headerName:
                field === BUDGET_BALANCE_TOTAL_FIELD
                    ? t('budgetBalance.totalAmount', { exercice })
                    : `${translateBlock(field)} ${exercice}`,
            cellStyle: ({
                data,
            }: {
                data: { [CHAPTER_LABEL_FIELD]: string };
            }) => {
                const colored = translatedIndicators.includes(
                    data[CHAPTER_LABEL_FIELD],
                );
                return {
                    backgroundColor: colored ? backgroundColor : '',
                };
            },
        })),
    ];
    if (isReportedAmountUsed) {
        const finalIndicatorColumnIndex = baseColumns.findIndex(
            (column) => column.field === finalIndicatorColumnField,
        );
        if (finalIndicatorColumnIndex !== -1) {
            baseColumns.splice(finalIndicatorColumnIndex + 1, 0, {
                ...numericColDef,
                field: REPORTED_FIELD,
                headerName: t('budgetOverview.reportedAmount'),
            });
            baseColumns.splice(finalIndicatorColumnIndex + 2, 0, {
                ...numericColDef,
                field: getFieldWithReportFromField(finalIndicatorColumnField),
                headerName: t('budgetOverview.totalWithReports'),
            });
        }
    }
    return baseColumns;
};
